import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Search, Layers, ListTree, Wallet, Scale, Receipt, ShieldCheck,
  Package, Users, User, Baby, Building2, Landmark, CreditCard,
  Plane, Hotel, Bus, Shield, Car, Ship, Luggage, FileText,
  FileMinus, FilePlus, FileX,
  ChevronRight, ChevronDown, ChevronLeft, ArrowLeft, X, Check, Pen,
  Ban, Lock, AlertTriangle, AlertCircle, Info, Calendar, Loader2,
  CircleCheckBig, Circle, XCircle, Hash,
} from "lucide-react";
import styles from "./CancellationFlow.module.css";
import {
  useGetBookings,
  useGetBookingDetail,
  useCancelBooking,
  useCancelBookingProduct,
  useCancelFlightLegs,
  useCreateTravelerModification,
  useCreateProductModification,
  useCreateCancellationCase,
} from "../../../api/booking/useBookingApi";
import { useToast } from "../../../context/ToastContext";

// ── Constants ────────────────────────────────────────────────────────────
const VAT_RATE = 0.21;
// The Sunsky cancellation fee is VAT-inclusive, so the VAT contained inside it is
// extracted with the inclusive fraction 21/121 (= 0.21 / 1.21), NOT 0.21 (which
// would be VAT added on top).
const VAT_INCLUSIVE_FRACTION = VAT_RATE / (1 + VAT_RATE);

const STEPS = [
  { id: 1, label: "Booking",   Icon: Search },
  { id: 2, label: "Scope",     Icon: Layers },
  { id: 3, label: "Items",     Icon: ListTree },
  { id: 4, label: "Pricing",   Icon: Wallet },
  { id: 5, label: "Financial", Icon: Scale },
  { id: 6, label: "Documents", Icon: Receipt },
  { id: 7, label: "Confirm",   Icon: ShieldCheck },
];

const SCOPES = [
  { key: "full",    title: "Full Booking",  Icon: FileText, desc: "Cancel the entire booking — every product, component and traveler" },
  { key: "product", title: "Products",      Icon: Package,  desc: "Cancel one or more whole products; the booking stays active if others remain" },
  { key: "partial", title: "Partial",       Icon: ListTree, desc: "Cancel specific components, travelers or SSRs within products" },
];

const CANCELLATION_REASONS = [
  "Customer request — change of plans",
  "Customer request — medical reason",
  "Customer request — visa refused",
  "Customer request — bereavement",
  "Supplier cancellation / failure",
  "Schedule change rejected by customer",
  "Force majeure / natural disaster",
  "Duplicate booking",
  "Agent error",
  "Other (see notes)",
];

const TYPE_ICON = {
  Flight: Plane, Hotel: Hotel, Transfer: Bus, Insurance: Shield,
  Package: Package, CarRental: Car, Visa: FileText, Cruise: Ship, Incoming: Luggage,
};
const PAX_ICON = { Adult: User, Child: User, Infant: Baby };

const KIND_ICON = {
  product: Package, leg: Plane, direction: Bus, room: Hotel,
  pax: User, ssr: Luggage,
};

// VAT-regime chips (the one permitted coloured status indicator)
const REGIME_META = {
  TOMS:        { label: "TOMS (margin VAT)", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  AGENT:       { label: "Agent (pass-through)", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  PASSTHROUGH: { label: "Pass-through",      color: "#0d7a4f", bg: "#f0fdf4", border: "#bbf7d0" },
  STANDARD:    { label: "Standard VAT",      color: "#d97706", bg: "#fff7ed", border: "#fed7aa" },
};

const STATUS_META = {
  Active:               { label: "Active",               cls: "stActive" },
  Confirmed:            { label: "Confirmed",            cls: "stActive" },
  Pending:              { label: "Pending",              cls: "stPending" },
  "Partially Cancelled":{ label: "Partially Cancelled",  cls: "stPending" },
  Cancelled:            { label: "Cancelled",            cls: "stCancelled" },
  // Payment statuses passed through StatusBadge on booking cards
  Paid:                 { label: "Paid",                 cls: "stActive" },
  Partial:              { label: "Partially Paid",       cls: "stPending" },
  Unpaid:               { label: "Unpaid",               cls: "stPending" },
  Refunded:             { label: "Refunded",             cls: "stActive" },
  "Waiting Supplier Refund": { label: "Waiting Supplier Refund", cls: "stPending" },
};

// ── Helpers ──────────────────────────────────────────────────────────────
const num = (x) => { const n = parseFloat(x); return Number.isFinite(n) ? n : 0; };
const fmt = (n) => `€ ${num(n).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const clamp = (n, lo, hi) => Math.min(Math.max(n, lo), hi);
const safeParse = (v) => { if (typeof v !== "string") return v || {}; try { return JSON.parse(v) || {}; } catch { return {}; } };
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? "—" : dt.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
};
const paxLabel = (t) => (t === "ADT" ? "Adult" : t === "CHD" ? "Child" : t === "INF" ? "Infant" : (t || "Adult"));

const productSell = (p, pd) => {
  const pr = pd.pricing || {};
  let sell = 0, cost = 0, vat = 0;
  if (p.productType === "Flight") {
    (pr.travelerPricing || []).forEach((tp) => {
      sell += num(tp.sellingPrice || tp.clientPrice);
      cost += num(tp.netPrice) + num(tp.tax);
      vat += num(tp.vatTax);
    });
  } else if (p.productType === "Hotel") {
    (pr.roomPricing || []).forEach((rp) => {
      const v = num(rp.vatTax || rp.vat);
      sell += num(rp.sellingPrice) + v; cost += num(rp.netPrice); vat += v;
    });
  } else {
    const tps = pr.travelerPricing || [];
    if (tps.length > 0) {
      tps.forEach((tp) => { sell += num(tp.sellingPrice); cost += num(tp.costPrice); vat += num(tp.vatTax); });
    } else {
      const v = num(pr.vatTax || pr.vat);
      sell += num(pr.sellingPrice) + v; cost += num(pr.costPrice); vat += v;
    }
  }
  return { sell, cost, vat };
};

const deriveRegime = (p) => {
  const role = (p.supplierCommercialRole || "").toUpperCase();
  if (role === "MERCHANT") return "TOMS";
  if (role === "AGENT") return "AGENT";
  return ["Hotel", "Transfer", "Package"].includes(p.productType) ? "TOMS" : "AGENT";
};

const productLabel = (p, pd) => {
  switch (p.productType) {
    case "Flight": {
      const codes = (pd.legs || []).map((l) => l.departureAirport).filter(Boolean);
      const last = pd.legs?.[pd.legs.length - 1]?.arrivalAirport;
      if (last) codes.push(last);
      return codes.length >= 2 ? codes.join(" → ") : "Flight";
    }
    case "Hotel": return pd.hotelName || pd.destination || "Hotel";
    case "Transfer": return pd.transferType ? `${pd.transferType} Transfer` : "Transfer";
    case "Insurance": return pd.insuranceName || pd.policyType || "Insurance";
    case "Package": return pd.packageName || pd.destination || "Package";
    case "CarRental": return [pd.pickupLocation, pd.vehicleMakeModel || pd.carCategory].filter(Boolean).join(" – ") || "Car Rental";
    case "Visa": return [pd.destinationCountry || pd.visaCountry, pd.visaType].filter(Boolean).join(" ") || "Visa";
    case "Cruise": return pd.shipName || pd.cruiseLine || "Cruise";
    default: return p.productType || "Product";
  }
};

/** Build the rich cancellation model (tree of products → entities) from raw detail. */
const buildModel = (b) => {
  if (!b) return null;
  const rawProducts = b.products || [];

  // Traveler identity + who is on which product. `assignments` is already on the
  // wire from the booking detail API — without it a flight leg cannot know which
  // travelers are actually on it, and the wizard offers travelers from other flights.
  const travelerById = {};
  (b.travelers || []).forEach((t) => {
    travelerById[String(t.id)] = {
      id: t.id,
      name: `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || "—",
      type: paxLabel(t.type),
      status: t.status || "Active",
    };
  });
  const assignedByProduct = {};
  (b.assignments || []).forEach((a) => {
    const k = String(a.productId);
    (assignedByProduct[k] ||= new Set()).add(String(a.travelerId));
  });

  const products = rawProducts.map((p) => {
    const pd = safeParse(p.productDetails);
    const { sell, cost, vat } = productSell(p, pd);
    const regime = deriveRegime(p);
    const entities = [];

    if (p.productType === "Flight") {
      const legs = pd.legs || [];
      const tp = pd.pricing?.travelerPricing || [];
      // SPLIT mode carries direction-specific pricing via travelerPricing[].legType.
      const isSplit = pd.flightPricingMode === "SPLIT" || tp.some((x) => x.legType);
      const cancelledDirs = pd.cancellation?.cancelledDirections || [];
      if (legs.length > 0) {
        // Group legs by DIRECTION (Outbound / Inbound). One-way collapses to a single
        // group that behaves as the whole flight; round-trip / multi yields one entity
        // per direction so the operator can cancel just the outbound or just the inbound.
        const order = [];
        const groups = {};
        legs.forEach((l, i) => {
          const dir = l.direction || (legs.length === 1 ? "Flight" : `Segment ${i + 1}`);
          if (!groups[dir]) { groups[dir] = { direction: dir, legs: [] }; order.push(dir); }
          groups[dir].legs.push(l);
        });
        order.forEach((dir) => {
          const g = groups[dir];
          let dSell, dCost, dVat;
          if (isSplit) {
            // Exact per-direction pricing from the matching legType rows.
            const rows = tp.filter((x) => (x.legType || "") === dir);
            dSell = rows.reduce((s, x) => s + num(x.sellingPrice || x.clientPrice), 0);
            dCost = rows.reduce((s, x) => s + num(x.netPrice) + num(x.tax), 0);
            dVat = rows.reduce((s, x) => s + num(x.vatTax), 0);
          } else {
            // FULL mode: one combined price → even split across directions (estimate).
            dSell = sell / order.length; dCost = cost / order.length; dVat = vat / order.length;
          }
          const route = g.legs.map((l) => `${l.departureAirport || "—"} → ${l.arrivalAirport || "—"}`).join(", ");
          // Who is on THIS direction, and what each of them is worth on it. This is
          // the (traveler × direction) cell the backend cancels: in SPLIT it is the
          // traveler's row for this legType; in FULL their single combined row is
          // split evenly across directions (an estimate — flagged `allocated`).
          // Falls back to the assignment rows so an assigned-but-unpriced traveler
          // is still offered rather than silently dropped.
          const dirTravelerIds = isSplit
            ? [...new Set(tp.filter((x) => (x.legType || "") === dir).map((x) => String(x.travelerId)))]
            : [...new Set(tp.map((x) => String(x.travelerId)))];
          const fallbackIds = [...(assignedByProduct[String(p.id)] || [])];
          const dirTravelers = (dirTravelerIds.length ? dirTravelerIds : fallbackIds)
            .filter((tid) => travelerById[tid] && travelerById[tid].status !== "Cancelled")
            .map((tid) => {
              const rows = isSplit
                ? tp.filter((x) => String(x.travelerId) === tid && (x.legType || "") === dir)
                : tp.filter((x) => String(x.travelerId) === tid);
              const share = isSplit ? 1 : (order.length || 1);
              return {
                ...travelerById[tid],
                sell: rows.reduce((s, x) => s + num(x.sellingPrice || x.clientPrice), 0) / share,
                cost: rows.reduce((s, x) => s + num(x.netPrice) + num(x.tax), 0) / share,
                vat: rows.reduce((s, x) => s + num(x.vatTax), 0) / share,
              };
            });
          entities.push({
            id: `${p.id}-dir-${dir}`, kind: "direction", direction: dir,
            legType: isSplit ? dir : null,
            legIds: g.legs.map((l) => l.id).filter(Boolean),
            travelers: dirTravelers,
            label: order.length > 1 ? `${dir} — ${route}` : (route || "Whole flight"),
            sub: g.legs.map((l) => [l.flightNumber, fmtDate(l.departureDate)].filter(Boolean).join(" · ")).filter(Boolean).join("  /  "),
            sell: dSell, cost: dCost, vat: dVat,
            allocated: !isSplit && order.length > 1,
            // Treat a direction as cancelled if it's recorded OR all its legs are already marked.
            cancelled: cancelledDirs.includes(dir) || g.legs.every((l) => l.flightStatus === "Cancelled" || l.legStatus === "Cancelled"),
          });
        });
      } else {
        entities.push({ id: `${p.id}-whole`, kind: "product", label: "Whole flight", sell, cost, vat });
      }
    } else if (p.productType === "Hotel") {
      const rooms = (Array.isArray(pd.rooms) ? pd.rooms : []);
      const rp = pd.pricing?.roomPricing || [];
      if (rooms.length > 0) {
        rooms.forEach((r, i) => {
          const rpr = rp.find((x) => x.roomId === r.id) || rp[i] || {};
          const v = num(rpr.vatTax || rpr.vat);
          entities.push({
            id: `${p.id}-room-${r.id || i}`, kind: "room",
            label: `Room ${i + 1}${r.roomType ? ` — ${r.roomType}` : ""}`,
            sub: [r.boardName, r.roomName].filter(Boolean).join(" · "),
            sell: num(rpr.sellingPrice) + v || sell / rooms.length,
            cost: num(rpr.netPrice) || cost / rooms.length, vat: v || vat / rooms.length,
          });
        });
      } else entities.push({ id: `${p.id}-whole`, kind: "product", label: "Whole hotel", sell, cost, vat });
    } else if (p.productType === "Transfer") {
      if (pd.transferMoment === "RoundTrip") {
        ["Arrival", "Departure"].forEach((dir, i) => entities.push({
          id: `${p.id}-dir-${dir}`, kind: "direction", label: `${dir} Transfer`,
          sell: sell / 2, cost: cost / 2, vat: vat / 2, allocated: true,
        }));
      } else entities.push({ id: `${p.id}-whole`, kind: "product", label: "Transfer", sell, cost, vat });
    } else {
      const tps = pd.pricing?.travelerPricing || [];
      if (tps.length > 1) {
        tps.forEach((tp, i) => entities.push({
          id: `${p.id}-pax-${tp.travelerId || i}`, kind: "pax",
          label: `Traveler ${i + 1}`, travelerId: tp.travelerId,
          sell: num(tp.sellingPrice), cost: num(tp.costPrice), vat: num(tp.vatTax),
        }));
      } else entities.push({ id: `${p.id}-whole`, kind: "product", label: `Whole ${p.productType}`, sell, cost, vat });
    }

    return {
      id: p.id, type: p.productType, label: productLabel(p, pd),
      supplier: p.supplierName || (p.supplierId ? `Supplier #${p.supplierId}` : "—"),
      regime, status: p.productStatus || "Active", sell, cost, vat, entities,
    };
  });

  const ssrs = (b.ssrs || []).map((s) => {
    const v = num(s.vatTax) * num(s.quantity || 1);
    return {
      id: `ssr-${s.id}`, productId: s.productId, travelerId: s.travelerId,
      label: `${s.ssrName || s.ssrCode || "SSR"}${s.quantity > 1 ? ` ×${s.quantity}` : ""}`,
      // SSR Fee is VAT-inclusive: sellingPrice (cost + fee) already contains the VAT — never add it on top.
      sell: num(s.sellingPrice) * num(s.quantity || 1),
      cost: num(s.costPrice) * num(s.quantity || 1), vat: v, status: s.status || "Active", kind: "ssr",
    };
  });

  // ── Per-traveler cancellable share ──────────────────────────────────────
  // The sum of a traveler's OWN priced lines: flight per-traveler pricing +
  // per-traveler generic products (Insurance/Car/Visa/Cruise/Package/Incoming) +
  // their SSRs. Hotel & Transfer are room/unit-priced, so cancelling one traveler
  // does NOT reduce them — they're excluded here to mirror recalculateBookingTotals,
  // which only drops a cancelled traveler's flight/generic/SSR pricing. This is the
  // "Original" value the operator prices the traveler cancellation against.
  const travShare = {};
  const addShare = (tid, sell, cost, vat) => {
    if (tid == null) return;
    const k = String(tid);
    if (!travShare[k]) travShare[k] = { sell: 0, cost: 0, vat: 0 };
    travShare[k].sell += num(sell); travShare[k].cost += num(cost); travShare[k].vat += num(vat);
  };
  rawProducts.forEach((p) => {
    if (p.productStatus === "Cancelled") return;
    const pd = safeParse(p.productDetails);
    const tps = pd.pricing?.travelerPricing || [];
    if (p.productType === "Flight") {
      tps.forEach((tp) => addShare(tp.travelerId, tp.sellingPrice || tp.clientPrice, num(tp.netPrice) + num(tp.tax), tp.vatTax));
    } else if (p.productType !== "Hotel" && p.productType !== "Transfer") {
      tps.forEach((tp) => addShare(tp.travelerId, tp.sellingPrice, tp.costPrice, tp.vatTax));
    }
  });
  ssrs.forEach((s) => { if (s.status !== "Cancelled") addShare(s.travelerId, s.sell, s.cost, s.vat); });

  const payments = b.payments || [];
  const clientPaid = payments
    .filter((p) => (p.paymentDirection !== "Outgoing" && p.direction !== "Outgoing") && (p.paymentStatus === "Completed" || p.paymentStatus === "Paid"))
    .reduce((s, p) => s + num(p.amount), 0);
  const supplierPaid = payments
    .filter((p) => (p.paymentDirection === "Outgoing" || p.direction === "Outgoing") && (p.paymentStatus === "Completed" || p.paymentStatus === "Paid"))
    .reduce((s, p) => s + num(p.amount), 0);

  const totalPrice = num(b.grandTotal || b.totalAmount);
  const amountPaid = num(b.paidAmount) || clientPaid;

  return {
    id: b.id, ref: b.bookingReference, invoiceNumber: `INV-${b.bookingReference || b.id}`,
    bookingStatus: b.status || "Pending", paymentStatus: b.paymentStatus || "Unpaid",
    isLocked: !!b.isLocked,
    customer: { name: b.customerName || "—", type: b.customerType || "Private" },
    totalPrice, amountPaid, supplierPaid, outstanding: Math.max(totalPrice - amountPaid, 0),
    products, ssrs,
    travelers: (b.travelers || []).map((t) => {
      const sh = travShare[String(t.id)] || { sell: 0, cost: 0, vat: 0 };
      return {
        id: t.id, name: `${t.firstName || ""} ${(t.lastName || "").toUpperCase()}`.trim() || "—",
        type: paxLabel(t.type), status: t.status || "Active",
        // Cancellable share (flight + per-traveler generic + SSRs), used as the
        // "Original" the traveler cancellation is priced against.
        sell: sh.sell, cost: sh.cost, vat: sh.vat,
      };
    }),
  };
};

// ── Traveler scoping ─────────────────────────────────────────────────────
// A flight leg is shared by everyone on it, so ticking a leg AND ticking travelers
// means "cancel this leg for these travelers only" — one action, not two. These two
// helpers are the single source of that rule; the Items tree, the pricing lines and
// the submit all read them so they can never disagree.

/** Flight-leg entities currently ticked that carry a traveler breakdown. */
const selectedDirEntities = (sel) =>
  Object.values(sel.entities || {}).filter((e) => e && e.kind === "direction" && (e.travelers || []).length > 0);

/** The distinct travelers on the ticked legs (empty when no such leg is ticked). */
const travelersInScope = (sel) => {
  const seen = new Set();
  const out = [];
  selectedDirEntities(sel).forEach((e) =>
    (e.travelers || []).forEach((t) => {
      if (seen.has(String(t.id))) return;
      seen.add(String(t.id));
      out.push(t);
    })
  );
  return out;
};

/** True once ticked travelers are narrowing a ticked leg rather than standing alone. */
const travelerScopeActive = (sel) =>
  selectedDirEntities(sel).length > 0 && Object.values(sel.travelers || {}).some(Boolean);

// ── Small atoms ──────────────────────────────────────────────────────────
const TypeChip = ({ type, size = 14 }) => { const I = TYPE_ICON[type] || FileText; return <span className={styles.typeChip}><I size={size} /></span>; };
const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.Pending;
  return <span className={`${styles.statusBadge} ${styles[m.cls]}`}>{m.label}</span>;
};
const RegimePill = ({ regime }) => {
  const m = REGIME_META[regime] || REGIME_META.STANDARD;
  return <span className={styles.regimePill} style={{ background: m.bg, color: m.color, border: `1px solid ${m.border}` }}>{m.label}</span>;
};

// ── Step Indicator ───────────────────────────────────────────────────────
const StepIndicator = ({ current, maxReached, onJump }) => (
  <div className={styles.stepper}>
    {STEPS.map((s, i) => {
      const done = current > s.id, active = current === s.id;
      const reachable = s.id <= maxReached;
      const Icon = s.Icon;
      return (
        <div key={s.id} className={styles.stepperItem}>
          <button type="button" className={styles.stepperInner} disabled={!reachable || active} onClick={() => reachable && onJump?.(s.id)} style={{ cursor: reachable && !active ? "pointer" : "default" }}>
            <div className={`${styles.stepBubble} ${active ? styles.stepActive : done ? styles.stepDone : ""}`}>{done ? <Check size={15} /> : <Icon size={15} />}</div>
            <span className={`${styles.stepLabel} ${active ? styles.stepLabelActive : done ? styles.stepLabelDone : ""}`}>{s.label}</span>
          </button>
          {i < STEPS.length - 1 && <div className={`${styles.stepConnector} ${done ? styles.connectorDone : ""}`} />}
        </div>
      );
    })}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// STEP 1 — Select Booking
// ══════════════════════════════════════════════════════════════════════════
const StepBooking = ({ query, setQuery, results, loading, error, selectedId, onSelect, model, detailLoading, detailError, locked }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.stepHeading}>
      <h2>{locked ? "Booking to Cancel" : "Select Booking"}</h2>
      <p>{locked ? "Review the booking below, then continue to choose what to cancel." : "Search by reference, customer name or email to open the cancellation workspace"}</p>
    </div>
    {!locked && (
      <>
        <div className={styles.searchBox}>
          <Search size={16} className={styles.searchIcon} />
          <input className={styles.searchInput} placeholder="e.g. SNK-2025-00198 or customer name…" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus />
          {query && <button className={styles.searchClear} onClick={() => setQuery("")} aria-label="Clear"><X size={14} /></button>}
        </div>
        <div className={styles.bookingList}>
          {loading && <div className={styles.listState}><Loader2 size={16} className={styles.spin} /> Searching…</div>}
          {!loading && error && <div className={styles.errorBanner}><AlertCircle size={15} /> {error}</div>}
          {!loading && !error && results.length === 0 && (
            <div className={styles.emptyState}><Search size={26} className={styles.emptyIcon} /><p className={styles.emptyTitle}>{query ? `No bookings match “${query}”` : "Search for a booking"}</p><p className={styles.emptySub}>Try a reference, customer name or email.</p></div>
          )}
          {!loading && results.map((b) => {
            const sel = selectedId === b.id, cancelled = b.status === "Cancelled";
            return (
              <button key={b.id} className={`${styles.bookingCard} ${sel ? styles.bookingCardSelected : ""} ${cancelled ? styles.rowDisabled : ""}`} onClick={() => !cancelled && onSelect(b.id)} disabled={cancelled}>
                <div className={styles.bcTop}>
                  <div className={styles.bcLeft}><TypeChip type={b.type} size={13} /><span className={styles.bcRef}>{b.ref}</span></div>
                  <div className={styles.bcRight}><StatusBadge status={cancelled ? "Cancelled" : b.paymentStatus} />{sel && <span className={styles.bcSelected}><Check size={12} /> Selected</span>}</div>
                </div>
                <div className={styles.bcCustomer}><span className={styles.bcCustomerName}>{b.customerName}</span><span className={styles.bcCustomerMeta}>{b.customerEmail}</span></div>
                <div className={styles.bcMeta}><span className={styles.bcMetaItem}><Calendar size={12} /> {fmtDate(b.bookingDate)}</span><span className={styles.bcMetaItem}><Package size={12} /> {b.productCount} product{b.productCount !== 1 ? "s" : ""}</span><span className={styles.bcTotal}>{fmt(b.totalPrice)}</span></div>
              </button>
            );
          })}
        </div>
      </>
    )}
    {detailLoading && <div className={styles.infoBanner}><Loader2 size={15} className={styles.spin} /> Loading booking…</div>}
    {detailError && <div className={styles.errorBanner}><AlertCircle size={15} /> {detailError}</div>}
    {model && model.bookingStatus === "Cancelled" && <div className={styles.errorBanner}><Ban size={15} /> This booking is already cancelled.</div>}
    {model && model.isLocked && model.bookingStatus !== "Cancelled" && <div className={styles.warnBanner}><Lock size={15} /> This booking is locked — an admin may need to unlock it before cancelling.</div>}
    {model && model.bookingStatus !== "Cancelled" && (
      <div className={styles.successBanner}><CircleCheckBig size={16} /><div><strong>{model.ref}</strong> · {model.customer.name} · {fmt(model.totalPrice)}{model.outstanding > 0 && <> · <span className={styles.amber}>{fmt(model.outstanding)} outstanding</span></>}</div></div>
    )}
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// STEP 2 — Scope
// ══════════════════════════════════════════════════════════════════════════
const StepScope = ({ model, scope, onScope }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.stepHeading}><h2>Cancellation Scope</h2><p>Choose how much of the booking to cancel</p></div>
    <div className={styles.scopeGrid}>
      {SCOPES.map((s) => {
        const Icon = s.Icon, active = scope === s.key;
        return (
          <button key={s.key} className={`${styles.scopeCard} ${active ? styles.scopeCardActive : ""}`} onClick={() => onScope(s.key)}>
            <span className={styles.scopeIcon}><Icon size={20} /></span>
            <div className={styles.scopeInfo}><div className={styles.scopeTitle}>{s.title}</div><div className={styles.scopeDesc}>{s.desc}</div></div>
            <span className={`${styles.scopeRadio} ${active ? styles.scopeRadioActive : ""}`}>{active && <Check size={12} />}</span>
          </button>
        );
      })}
    </div>
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}><div className={styles.sectionTitle}><Info size={16} /> Booking overview</div><StatusBadge status={model.bookingStatus} /></div>
      <div className={styles.overviewGrid}>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Invoice</div><div className={styles.ovValue}>{model.invoiceNumber}</div></div>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Products</div><div className={styles.ovValue}>{model.products.length}</div></div>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Travelers</div><div className={styles.ovValue}>{model.travelers.length}</div></div>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Booking value</div><div className={styles.ovValue}>{fmt(model.totalPrice)}</div></div>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Paid</div><div className={styles.ovValue}>{fmt(model.amountPaid)}</div></div>
        <div className={styles.ovItem}><div className={styles.ovLabel}>Outstanding</div><div className={`${styles.ovValue} ${model.outstanding > 0 ? styles.amber : ""}`}>{fmt(model.outstanding)}</div></div>
      </div>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// STEP 3 — Items (cancellation tree)
// ══════════════════════════════════════════════════════════════════════════
const StepItems = ({ model, scope, sel, onToggleProduct, onToggleEntity, onToggleTraveler, onToggleSsr, selectedCount }) => {
  const [open, setOpen] = useState(() => new Set(model.products.map((p) => p.id)));
  const toggleOpen = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (scope === "full") {
    return (
      <div className={styles.stepWrapper}>
        <div className={styles.stepHeading}><h2>Items to Cancel</h2><p>The entire booking will be cancelled — all products, components and travelers</p></div>
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}><div className={styles.sectionTitle}><FileText size={16} /> Full booking cancellation</div></div>
          <div className={styles.treeBody}>
            {model.products.map((p) => (
              <div key={p.id} className={styles.treeRow}>
                <TypeChip type={p.type} />
                <span className={styles.treeLabel}>{p.label}</span>
                <RegimePill regime={p.regime} />
                <span className={styles.treeAmt}>{fmt(p.sell)}</span>
              </div>
            ))}
            {model.ssrs.map((s) => (
              <div key={s.id} className={styles.treeRow}><span className={styles.typeChip}><Luggage size={14} /></span><span className={styles.treeLabel}>{s.label}</span><span className={styles.treeAmt}>{fmt(s.sell)}</span></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Ticking a flight leg narrows the Travelers card to the people actually on it —
  // offering travelers from another flight was meaningless, and ticking them priced
  // the leg twice.
  const inScope = travelersInScope(sel);
  const scopedToLegs = inScope.length > 0;
  const travelerRows = scopedToLegs ? inScope : model.travelers;

  return (
    <div className={styles.stepWrapper}>
      <div className={styles.stepHeading}>
        <h2>Items to Cancel</h2>
        <p>{scope === "product" ? "Select whole products to cancel" : "Expand products and select components, travelers or SSRs"}</p>
      </div>
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}><div className={styles.sectionTitle}><ListTree size={16} /> Cancellation tree</div><span className={styles.countPill}>{selectedCount} selected</span></div>
        <div className={styles.treeBody}>
          {model.products.map((p) => {
            const cancelled = p.status === "Cancelled";
            const prodSel = !!sel.products[p.id];
            const expandable = scope === "partial" && (p.entities.length > 1 || model.ssrs.some((s) => s.productId === p.id));
            const isOpen = open.has(p.id);
            // A flight is cancelled leg by leg, never as a lump — so it has no
            // product checkbox here: the operator picks Outbound / Inbound. That is
            // also the only path that carries per-traveler scoping (ticking the whole
            // product routes through cancelProduct, which cannot express "for Ada
            // only"). Cancelling every leg still rolls the flight up to Cancelled.
            // Product scope doesn't render legs at all, so the checkbox stays there.
            const legChoiceOnly = scope === "partial" && p.type === "Flight" && p.entities.some((e) => e.kind === "direction");
            return (
              <div key={p.id} className={`${styles.treeProduct} ${cancelled ? styles.rowDisabled : ""}`}>
                <div className={styles.treeProductHead}>
                  {expandable ? (
                    <button className={styles.treeExpand} onClick={() => toggleOpen(p.id)}>{isOpen ? <ChevronDown size={15} /> : <ChevronRight size={15} />}</button>
                  ) : <span className={styles.treeExpandSpacer} />}
                  {legChoiceOnly ? (
                    <span className={styles.treeExpandSpacer} />
                  ) : (
                    <span className={`${styles.checkbox} ${prodSel ? styles.checkboxActive : ""}`} onClick={() => !cancelled && onToggleProduct(p.id)}>{prodSel && <Check size={12} />}</span>
                  )}
                  <TypeChip type={p.type} />
                  <div className={styles.treeProductInfo}>
                    <div className={styles.treeProductTop}><span className={styles.treeLabel}>{p.label}</span><span className={styles.typeTag}>{p.type}</span>{cancelled && <span className={styles.cancelledTag}>Cancelled</span>}</div>
                    <div className={styles.treeProductSub}>{p.supplier}</div>
                  </div>
                  <RegimePill regime={p.regime} />
                  <span className={styles.treeAmt}>{fmt(p.sell)}</span>
                </div>
                {scope === "partial" && isOpen && (
                  <div className={styles.treeChildren}>
                    {p.entities.length > 1 && p.entities.map((e) => {
                      const eSel = !!sel.entities[e.id];
                      const eCancelled = !!e.cancelled;
                      const EIcon = KIND_ICON[e.kind] || Circle;
                      return (
                        <div key={e.id} className={`${styles.treeChild} ${eSel ? styles.treeChildSel : ""} ${eCancelled ? styles.rowDisabled : ""}`} onClick={() => !eCancelled && onToggleEntity(p.id, e)}>
                          <span className={`${styles.checkbox} ${eSel ? styles.checkboxActive : ""}`}>{eSel && <Check size={11} />}</span>
                          <span className={styles.childIcon}><EIcon size={13} /></span>
                          <div className={styles.childInfo}><span className={styles.childLabel}>{e.label}</span>{e.sub && <span className={styles.childSub}>{e.sub}</span>}</div>
                          {eCancelled && <span className={styles.cancelledTag}>Cancelled</span>}
                          {e.allocated && !eCancelled && <span className={styles.allocTag}>allocated</span>}
                          <span className={styles.treeAmt}>{fmt(e.sell)}</span>
                        </div>
                      );
                    })}
                    {model.ssrs.filter((s) => s.productId === p.id).map((s) => {
                      const sSel = !!sel.ssrs[s.id];
                      return (
                        <div key={s.id} className={`${styles.treeChild} ${sSel ? styles.treeChildSel : ""}`} onClick={() => onToggleSsr(p.id, s)}>
                          <span className={`${styles.checkbox} ${sSel ? styles.checkboxActive : ""}`}>{sSel && <Check size={11} />}</span>
                          <span className={styles.childIcon}><Luggage size={13} /></span>
                          <div className={styles.childInfo}><span className={styles.childLabel}>{s.label}</span><span className={styles.childSub}>SSR</span></div>
                          <span className={styles.treeAmt}>{fmt(s.sell)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {scope === "partial" && travelerRows.length > 0 && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Users size={16} /> {scopedToLegs ? "Travelers on the selected flight leg(s)" : "Travelers"}
            </div>
            {scopedToLegs && <span className={styles.countPill}>{travelerRows.length} on this leg</span>}
          </div>
          <div className={styles.treeBody}>
            {travelerRows.map((t) => {
              const cancelled = t.status === "Cancelled", tSel = !!sel.travelers[t.id];
              const PI = PAX_ICON[t.type] || User;
              return (
                <div key={t.id} className={`${styles.treeRow} ${styles.treeRowSelectable} ${tSel ? styles.treeChildSel : ""} ${cancelled ? styles.rowDisabled : ""}`} onClick={() => !cancelled && onToggleTraveler(t.id)}>
                  <span className={`${styles.checkbox} ${tSel ? styles.checkboxActive : ""}`}>{tSel && <Check size={11} />}</span>
                  <span className={styles.typeChip}><PI size={14} /></span>
                  <span className={styles.treeLabel}>{t.name}</span>
                  <span className={styles.typeTag}>{t.type}</span>
                  {cancelled && <span className={styles.cancelledTag}>Cancelled</span>}
                  {!cancelled && <span className={styles.treeAmt}>{fmt(t.sell)}</span>}
                </div>
              );
            })}
          </div>
          <div className={styles.infoNote}><Info size={13} /> {scopedToLegs
            ? "Only travelers on the selected flight leg(s) are shown, priced at their share of those legs. Ticking them cancels those legs for those travelers only — everyone else keeps flying. Tick none to cancel the leg for all of them."
            : "Travelers are cancelled individually as traveler modifications. Their flight / per-traveler product / SSR share is shown above; enter the supplier cost and Sunsky fee on the next step. Booking and unaffected products remain active."}</div>
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// STEP 4 — Cancellation Pricing
// ══════════════════════════════════════════════════════════════════════════
const FeeInput = ({ value, onChange, max }) => (
  <input type="number" min={0} max={max} step={1} className={styles.numInput} value={value} onChange={(e) => onChange(e.target.value)} placeholder="0" />
);

const StepPricing = ({ lines, fees, onFee }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.stepHeading}><h2>Cancellation Pricing</h2><p>Enter the supplier cancellation cost and the Sunsky cancellation fee per item — the fee is VAT-inclusive (21%), so VAT is shown inside it, not added on top</p></div>
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}><div className={styles.sectionTitle}><Wallet size={16} /> Per-item cancellation pricing</div></div>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead><tr>
            <th>Item</th><th className={styles.tr}>Original</th><th className={styles.tr}>Supplier Cancellation Cost</th><th className={styles.tr}>Cancellation Fee</th><th className={styles.tr}>VAT</th><th className={styles.tr}>Total Canc.</th><th className={styles.tr}>Refund</th>
          </tr></thead>
          <tbody>
            {lines.map((ln) => {
              const f = fees[ln.id] || { cost: "", fee: "" };
              const cc = clamp(num(f.cost), 0, Infinity);
              const cf = clamp(num(f.fee), 0, Infinity);
              const cv = cf * VAT_INCLUSIVE_FRACTION;   // VAT contained within the VAT-inclusive fee
              const total = cc + cf;       // fee already includes VAT — never add it on top
              const refund = ln.sell - total;
              const EIcon = KIND_ICON[ln.kind] || Package;
              return (
                <tr key={ln.id}>
                  <td><div className={styles.tProduct}><span className={styles.typeChip}><EIcon size={13} /></span><div><div className={styles.tLabel}>{ln.label}</div><div className={styles.tSub}>{ln.productLabel}</div></div></div></td>
                  <td className={`${styles.tr} ${styles.mono}`}>{fmt(ln.sell)}</td>
                  <td className={styles.tr}><FeeInput value={f.cost} onChange={(v) => onFee(ln.id, { ...f, cost: v })} /></td>
                  <td className={styles.tr}><FeeInput value={f.fee} onChange={(v) => onFee(ln.id, { ...f, fee: v })} /></td>
                  <td className={`${styles.tr} ${styles.mono}`}>{fmt(cv)}</td>
                  <td className={`${styles.tr} ${styles.mono} ${styles.danger}`}>{fmt(total)}</td>
                  <td className={`${styles.tr} ${styles.mono} ${refund >= 0 ? styles.positive : styles.danger}`}>{refund < 0 ? `(${fmt(-refund)})` : fmt(refund)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={styles.infoNote}><Info size={13} /> Original prices are never overwritten. Refund in (brackets) means an additional amount is due from the customer. Allocated amounts on multi-leg / round-trip products are even-split suggestions — adjust the cancellation cost as provided by the supplier.</div>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// STEP 5 — Financial Impact
// ══════════════════════════════════════════════════════════════════════════
const StepFinancial = ({ model, totals }) => (
  <div className={styles.stepWrapper}>
    <div className={styles.stepHeading}><h2>Financial Impact</h2><p>Client and supplier impact of this cancellation</p></div>

    <div className={styles.moneyStrip}>
      <div className={styles.moneyCard}><div className={styles.moneyLabel}>Cancelled value</div><div className={styles.moneyValue}>{fmt(totals.cancelledSelling)}</div><div className={styles.moneySub}>Original selling cancelled</div></div>
      <div className={styles.moneyCard}><div className={styles.moneyLabel}>Cancellation amount</div><div className={`${styles.moneyValue} ${styles.danger}`}>{fmt(totals.cancellationTotal)}</div><div className={styles.moneySub}>Cost + fee (VAT incl.)</div></div>
      <div className={`${styles.moneyCard} ${styles.moneyCardAccent}`}>
        <div className={styles.moneyLabel}>{totals.net >= 0 ? "Refund due" : "Additional due"}</div>
        <div className={`${styles.moneyValue} ${totals.net >= 0 ? styles.positive : styles.danger}`}>{fmt(Math.abs(totals.net))}</div>
        <div className={styles.moneySub}>{totals.net >= 0 ? "Back to customer" : "Owed by customer"}</div>
      </div>
    </div>

    <div className={styles.impactGrid}>
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}><div className={styles.sectionTitle}><User size={16} /> Client impact</div></div>
        <div className={styles.kvBody}>
          <div className={styles.kvRow}><span>Amount paid</span><strong>{fmt(model.amountPaid)}</strong></div>
          <div className={styles.kvRow}><span>Cancellation amount</span><strong className={styles.danger}>{fmt(totals.cancellationTotal)}</strong></div>
          <div className={styles.kvRow}><span>Refund due</span><strong className={styles.positive}>{fmt(totals.refundDue)}</strong></div>
          <div className={styles.kvRow}><span>Additional due</span><strong className={styles.danger}>{fmt(totals.additionalDue)}</strong></div>
          <div className={`${styles.kvRow} ${styles.kvTotal}`}><span>Status</span><strong>{totals.refundDue > 0 ? "Refund Due" : totals.additionalDue > 0 ? "Additional Payment Required" : "Settled"}</strong></div>
        </div>
      </div>
      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}><div className={styles.sectionTitle}><Building2 size={16} /> Supplier impact</div></div>
        <div className={styles.kvBody}>
          <div className={styles.kvRow}><span>Supplier cost (cancelled)</span><strong>{fmt(totals.cancelledCost)}</strong></div>
          <div className={styles.kvRow}><span>Supplier paid</span><strong>{fmt(model.supplierPaid)}</strong></div>
          <div className={styles.kvRow}><span>Supplier penalty</span><strong className={styles.danger}>{fmt(totals.cancellationCost)}</strong></div>
          <div className={styles.kvRow}><span>Supplier refund due</span><strong className={styles.positive}>{fmt(Math.max(totals.cancelledCost - totals.cancellationCost, 0))}</strong></div>
          <div className={`${styles.kvRow} ${styles.kvTotal}`}><span>Net exposure</span><strong>{fmt(totals.cancellationCost)}</strong></div>
        </div>
      </div>
    </div>

    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}><div className={styles.sectionTitle}><Scale size={16} /> Totals</div></div>
      <div className={styles.kvBody}>
        <div className={styles.kvRow}><span>Total cancellation cost</span><strong>{fmt(totals.cancellationCost)}</strong></div>
        <div className={styles.kvRow}><span>Total cancellation fee</span><strong>{fmt(totals.cancellationFee)}</strong></div>
        <div className={styles.kvRow}><span>VAT (included in fee)</span><strong>{fmt(totals.cancellationVat)}</strong></div>
        <div className={`${styles.kvRow} ${styles.kvTotal}`}><span>Total cancellation amount</span><strong className={styles.danger}>{fmt(totals.cancellationTotal)}</strong></div>
      </div>
    </div>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════
// STEP 6 — Document Decision
// ══════════════════════════════════════════════════════════════════════════
const StepDocuments = ({ model, totals, refundMethod, onRefundMethod, reason, onReason, notes, onNotes }) => {
  const doc = totals.net > 0.005 ? "credit" : totals.net < -0.005 ? "debit" : "none";
  const docNum = doc === "credit" ? `CN-${model.invoiceNumber.replace(/^INV-/, "")}/1` : doc === "debit" ? `DN-${model.invoiceNumber.replace(/^INV-/, "")}/1` : "—";
  const DocIcon = doc === "credit" ? FileMinus : doc === "debit" ? FilePlus : FileX;
  return (
    <div className={styles.stepWrapper}>
      <div className={styles.stepHeading}><h2>Financial Document</h2><p>The system determines the financial document automatically from the net impact</p></div>

      <div className={`${styles.docDecision} ${doc === "credit" ? styles.docCredit : doc === "debit" ? styles.docDebit : styles.docNone}`}>
        <div className={styles.docIcon}><DocIcon size={30} /></div>
        <div className={styles.docInfo}>
          <div className={styles.docTitle}>{doc === "credit" ? "Credit Note" : doc === "debit" ? "Debit Note" : "No financial document"}</div>
          <div className={styles.docSub}>
            {doc === "credit" ? `Customer is refunded ${fmt(totals.net)} — a credit note is issued against ${model.invoiceNumber}.`
              : doc === "debit" ? `Customer owes an additional ${fmt(-totals.net)} — a debit note is issued against ${model.invoiceNumber}.`
              : "Net impact is zero — only a cancellation case is recorded."}
          </div>
        </div>
        {doc !== "none" && <div className={styles.docNum}><Hash size={13} /> {docNum}</div>}
      </div>

      {doc === "credit" && (
        <div className={styles.sectionCard}>
          <div className={styles.sectionHeader}><div className={styles.sectionTitle}><CreditCard size={16} /> Refund method</div></div>
          <div className={styles.refundMethods}>
            {[
              { key: "original", label: "Original payment method", Icon: CreditCard, desc: "Processed in Finance within 5–10 business days" },
              { key: "credit_note", label: "Keep as credit balance", Icon: FileMinus, desc: "Usable on a future booking" },
              { key: "bank", label: "Bank transfer", Icon: Landmark, desc: "Requires customer bank details" },
            ].map((m) => {
              const active = refundMethod === m.key, MI = m.Icon;
              return (
                <label key={m.key} className={`${styles.refundMethodCard} ${active ? styles.refundMethodActive : ""}`}>
                  <input type="radio" name="rm" checked={active} onChange={() => onRefundMethod(m.key)} className={styles.radioHidden} />
                  <span className={styles.rmIcon}><MI size={18} /></span>
                  <div className={styles.rmInfo}><div className={styles.rmLabel}>{m.label}</div><div className={styles.rmDesc}>{m.desc}</div></div>
                  {active && <span className={styles.rmCheck}><Check size={16} /></span>}
                </label>
              );
            })}
          </div>
        </div>
      )}

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}><div className={styles.sectionTitle}><Pen size={16} /> Reason & notes</div></div>
        <div className={styles.formField}>
          <label>Reason for cancellation <span className={styles.req}>*</span></label>
          <select className={styles.selectInput} value={reason} onChange={(e) => onReason(e.target.value)}>
            <option value="">Select a reason…</option>
            {CANCELLATION_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div className={styles.formField} style={{ marginTop: 12 }}>
          <label>Agent notes {reason === "Other (see notes)" && <span className={styles.req}>*</span>}</label>
          <textarea className={styles.textareaInput} rows={3} placeholder="Additional notes for the cancellation case…" value={notes} onChange={(e) => onNotes(e.target.value)} />
        </div>
      </div>
      <div className={styles.refundIntentNote}><Info size={13} /> Confirming records the cancellation case, issues the Credit/Debit Note, and creates the pending refund record(s) automatically. The actual payout / supplier receipt is then executed in <strong>Finance → Payments</strong>; a cancelled booking shows <strong>"Waiting Supplier Refund"</strong> until the supplier refund is received.</div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// STEP 7 — Confirmation + Completion
// ══════════════════════════════════════════════════════════════════════════
const COMPLETION_ITEMS = [
  { key: "case", label: "Cancellation Case", exec: true },
  { key: "product", label: "Product / Component statuses", exec: true },
  { key: "traveler", label: "Traveler statuses", exec: true },
  { key: "totals", label: "Booking totals recalculated", exec: true },
  { key: "audit", label: "Audit trail", exec: true },
  { key: "pricing", label: "Pricing tab snapshot", exec: false },
  { key: "vat", label: "VAT recalculation", exec: false },
  { key: "cn", label: "Credit / Debit Note generation", exec: false },
  { key: "docs", label: "Financial documents", exec: false },
  { key: "vouchers", label: "Vouchers (flight / hotel / transfer)", exec: false },
  { key: "confirmation", label: "Travel confirmation version", exec: false },
  { key: "supplier", label: "Supplier payment ledger", exec: false },
  { key: "client", label: "Client payment ledger", exec: false },
  { key: "center", label: "Document center", exec: false },
  { key: "reporting", label: "Reporting", exec: false },
];

const StepConfirm = ({ model, totals, lines, scope, reason, doc, docNum, onConfirm, submitting, result }) => {
  const caseNo = `CAN-${(model.ref || "").replace(/^SNK-?/, "")}/1`;

  if (result) {
    const ok = result.ok;
    return (
      <div className={styles.stepWrapper}>
        <div className={styles.resultPanel}>
          <div className={`${styles.resultIcon} ${ok ? styles.resultIconSuccess : styles.resultIconWarn}`}>{ok ? <CircleCheckBig size={44} /> : <AlertTriangle size={44} />}</div>
          <h2 className={styles.resultTitle}>{ok ? "Cancellation Completed" : "Cancellation Partially Completed"}</h2>
          <p className={styles.resultMsg}>Cancellation case <strong>{caseNo}</strong> · {result.okCount} operation{result.okCount !== 1 ? "s" : ""} executed{result.failCount ? `, ${result.failCount} failed` : ""}.</p>

          <div className={styles.completionGrid}>
            {COMPLETION_ITEMS.map((it) => {
              const state = result.steps[it.key] || (it.exec ? "skipped" : "pending");
              return (
                <div key={it.key} className={`${styles.completionItem} ${state === "done" ? styles.cmpDone : state === "failed" ? styles.cmpFailed : styles.cmpPending}`}>
                  {state === "done" ? <CircleCheckBig size={14} /> : state === "failed" ? <XCircle size={14} /> : <Circle size={14} />}
                  <span>{it.label}</span>
                  {!it.exec && state === "pending" && <span className={styles.cmpTag}>pending backend</span>}
                </div>
              );
            })}
          </div>

          {result.failures?.length > 0 && (
            <div className={styles.noteList}>{result.failures.map((f, i) => <div key={i} className={styles.failItem}><XCircle size={14} /> {f}</div>)}</div>
          )}
          {result.warnings?.length > 0 && (
            <div className={styles.noteList}>{result.warnings.map((w, i) => <div key={i} className={styles.warnItem}><AlertCircle size={14} /> {w}</div>)}</div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.stepWrapper}>
      <div className={styles.stepHeading}><h2>Confirm Cancellation</h2><p>Review the cancellation case — this action cannot be undone</p></div>

      <div className={styles.dangerBanner}><AlertTriangle size={16} /><div><strong>This action is irreversible.</strong> Confirming creates the cancellation case, updates statuses and totals, and records the {doc === "credit" ? "credit note" : doc === "debit" ? "debit note" : "cancellation"} decision.</div></div>

      <div className={styles.confirmGrid}>
        <div className={styles.confirmSection}>
          <div className={styles.confirmSectionTitle}><Hash size={15} /> Cancellation Case</div>
          <div className={styles.confirmRow}><span>Case</span><strong>{caseNo}</strong></div>
          <div className={styles.confirmRow}><span>Booking</span><strong>{model.ref}</strong></div>
          <div className={styles.confirmRow}><span>Type</span><strong>{scope === "full" ? "Full Booking" : scope === "product" ? "Product" : "Partial"}</strong></div>
          <div className={styles.confirmRow}><span>Lines</span><strong>{lines.length}</strong></div>
          <div className={styles.confirmRow}><span>Reason</span><strong>{reason}</strong></div>
        </div>
        <div className={styles.confirmSection}>
          <div className={styles.confirmSectionTitle}><Scale size={15} /> Financials</div>
          <div className={styles.confirmRow}><span>Cancellation amount</span><strong className={styles.danger}>{fmt(totals.cancellationTotal)}</strong></div>
          <div className={styles.confirmRow}><span>Refund due</span><strong className={styles.positive}>{fmt(totals.refundDue)}</strong></div>
          <div className={styles.confirmRow}><span>Additional due</span><strong className={styles.danger}>{fmt(totals.additionalDue)}</strong></div>
          <div className={styles.confirmRow}><span>Document</span><strong className={styles.inlineIcon}>{doc === "credit" ? <FileMinus size={13} /> : doc === "debit" ? <FilePlus size={13} /> : <FileX size={13} />} {doc === "none" ? "None" : docNum}</strong></div>
        </div>
      </div>

      <div className={styles.sectionCard}>
        <div className={styles.sectionHeader}><div className={styles.sectionTitle}><ListTree size={16} /> Cancellation case lines</div></div>
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead><tr><th>Item</th><th className={styles.tr}>Original</th><th className={styles.tr}>Cancellation</th><th className={styles.tr}>Refund</th></tr></thead>
            <tbody>
              {lines.map((ln) => {
                const EIcon = KIND_ICON[ln.kind] || Package;
                return (
                  <tr key={ln.id}>
                    <td><div className={styles.tProduct}><span className={styles.typeChip}><EIcon size={13} /></span><div><div className={styles.tLabel}>{ln.label}</div><div className={styles.tSub}>{ln.productLabel}</div></div></div></td>
                    <td className={`${styles.tr} ${styles.mono}`}>{fmt(ln.sell)}</td>
                    <td className={`${styles.tr} ${styles.mono} ${styles.danger}`}>{fmt(ln.cancTotal)}</td>
                    <td className={`${styles.tr} ${styles.mono} ${ln.refund >= 0 ? styles.positive : styles.danger}`}>{ln.refund < 0 ? `(${fmt(-ln.refund)})` : fmt(ln.refund)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className={styles.confirmAction}>
        <div className={styles.confirmActionText}>By clicking <strong>Confirm Cancellation</strong>, the cancellation case is recorded and all executable operations run immediately.</div>
        <button className={styles.confirmBtn} onClick={onConfirm} disabled={submitting}>{submitting ? <><Loader2 size={14} className={styles.spin} /> Processing…</> : <><Ban size={14} /> Confirm Cancellation</>}</button>
      </div>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════
// SUMMARY PANEL
// ══════════════════════════════════════════════════════════════════════════
const SummaryPanel = ({ model, scope, step, selectedCount, totals }) => (
  <aside className={styles.summaryPanel}>
    <div className={styles.spHeader}><ListTree size={15} /> Cancellation Case</div>
    {!model ? <div className={styles.spEmpty}>No booking selected</div> : (
      <div className={styles.spBody}>
        <div className={styles.spSection}><div className={styles.spLabel}>Booking</div><div className={styles.spValue}>{model.ref}</div><div className={styles.spSub}>{model.invoiceNumber}</div></div>
        <div className={styles.spSection}><div className={styles.spLabel}>Customer</div><div className={styles.spValue}>{model.customer.name}</div></div>
        {step >= 2 && <div className={styles.spSection}><div className={styles.spLabel}>Scope</div><div className={styles.spValue}>{scope === "full" ? "Full Booking" : scope === "product" ? "Products" : "Partial"}</div></div>}
        {step >= 3 && <div className={styles.spSection}><div className={styles.spLabel}>Items selected</div><div className={styles.spValue}>{selectedCount}</div></div>}
        {step >= 4 && totals && (
          <>
            <div className={styles.spSection}><div className={styles.spLabel}>Cancellation amount</div><div className={`${styles.spValue} ${styles.danger}`}>{fmt(totals.cancellationTotal)}</div></div>
            <div className={styles.spSection}><div className={styles.spLabel}>{totals.net >= 0 ? "Refund due" : "Additional due"}</div><div className={`${styles.spValue} ${totals.net >= 0 ? styles.positive : styles.danger}`}>{fmt(Math.abs(totals.net))}</div></div>
          </>
        )}
      </div>
    )}
    <div className={styles.spSteps}>
      {STEPS.map((s) => (
        <div key={s.id} className={`${styles.spStep} ${step > s.id ? styles.spStepDone : step === s.id ? styles.spStepActive : ""}`}>
          <span className={styles.spStepDot}>{step > s.id && <Check size={9} />}</span><span>{s.label}</span>
        </div>
      ))}
    </div>
  </aside>
);

// ══════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════
const INITIAL_SEL = { products: {}, entities: {}, travelers: {}, ssrs: {} };

const CancellationFlow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const prefill = location.state?.booking || null;

  const [step, setStep] = useState(1);
  const [maxReached, setMaxReached] = useState(1);
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [selectedId, setSelectedId] = useState(prefill?.id || null);
  const [scope, setScope] = useState("full");
  const [sel, setSel] = useState(INITIAL_SEL);
  const [fees, setFees] = useState({});
  const [refundMethod, setRefundMethod] = useState("original");
  const [reason, setReason] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { const t = setTimeout(() => setDebounced(query.trim()), 350); return () => clearTimeout(t); }, [query]);
  useEffect(() => { if (prefill && !prefill.id && prefill.ref) setQuery(prefill.ref); }, [prefill]);

  const { data: rawList, loading: listLoading, error: listError } = useGetBookings({ page: 1, limit: 8, search: debounced || undefined });
  const results = useMemo(() => (rawList || []).map((b) => ({
    id: b.id, ref: b.bookingReference, type: (b.products && b.products[0]?.productType) || "Booking",
    status: b.status || "Pending", paymentStatus: b.paymentStatus || "Unpaid",
    customerName: b.customerName || "—", customerEmail: b.customerEmail || "—",
    bookingDate: b.bookingDate || b.createdAt, productCount: (b.products || []).length,
    totalPrice: num(b.grandTotal || b.totalAmount),
  })), [rawList]);

  const { data: detailData, loading: detailLoading, error: detailError } = useGetBookingDetail(selectedId);
  const model = useMemo(() => buildModel(detailData), [detailData]);

  const prevId = useRef(selectedId);
  useEffect(() => {
    if (prevId.current !== selectedId) { prevId.current = selectedId; setScope("full"); setSel(INITIAL_SEL); setFees({}); setReason(""); setNotes(""); setResult(null); }
  }, [selectedId]);

  // When launched from the Booking List or Booking Detail for a specific booking,
  // skip the search step and open that booking's cancellation workspace directly.
  // Step 1 stays locked to the passed booking; we advance once its detail loads
  // and it's actually cancellable (not already cancelled / not locked).
  const deepLinked = !!prefill?.id;
  const didAutoAdvance = useRef(false);
  useEffect(() => {
    if (deepLinked && !didAutoAdvance.current && step === 1 && model && model.bookingStatus !== "Cancelled" && !model.isLocked && !detailLoading) {
      didAutoAdvance.current = true;
      setStep(2);
      setMaxReached((m) => Math.max(m, 2));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [deepLinked, step, model, detailLoading]);

  const onScope = useCallback((v) => { setScope(v); setSel(INITIAL_SEL); setFees({}); }, []);
  const goStep = (n) => { setStep(n); setMaxReached((m) => Math.max(m, n)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // ── Selection toggles ──
  const onToggleProduct = (id) => setSel((s) => ({ ...s, products: { ...s.products, [id]: !s.products[id] } }));
  const onToggleEntity = (pid, e) => setSel((s) => {
    const next = { ...s, entities: { ...s.entities, [e.id]: s.entities[e.id] ? undefined : { ...e, productId: pid } } };
    // Drop traveler ticks that the new selection no longer covers. Without this,
    // unticking a leg would silently promote "cancel this leg for Ada" into
    // "cancel Ada from the whole booking" — the same tick meaning something far larger.
    const allowed = new Set(travelersInScope(next).map((t) => String(t.id)));
    if (allowed.size > 0) {
      next.travelers = Object.fromEntries(
        Object.entries(next.travelers).filter(([tid, on]) => on && allowed.has(String(tid)))
      );
    } else if (selectedDirEntities(s).length > 0) {
      // Last scoping leg just came off — clear the ticks it was scoping.
      next.travelers = {};
    }
    return next;
  });
  const onToggleTraveler = (id) => setSel((s) => ({ ...s, travelers: { ...s.travelers, [id]: !s.travelers[id] } }));
  const onToggleSsr = (pid, e) => setSel((s) => ({ ...s, ssrs: { ...s.ssrs, [e.id]: s.ssrs[e.id] ? undefined : { ...e, productId: pid } } }));

  // ── Build the flat list of cancellation lines from scope + selection ──
  const lines = useMemo(() => {
    if (!model) return [];
    const out = [];
    if (scope === "full") {
      model.products.filter((p) => p.status !== "Cancelled").forEach((p) =>
        out.push({ id: `prod-${p.id}`, productId: p.id, kind: "product", label: p.label, productLabel: p.type, sell: p.sell, cost: p.cost, vat: p.vat, regime: p.regime }));
      model.ssrs.filter((s) => s.status !== "Cancelled").forEach((s) =>
        out.push({ id: s.id, productId: s.productId, kind: "ssr", label: s.label, productLabel: "SSR", sell: s.sell, cost: s.cost, vat: s.vat }));
    } else if (scope === "product") {
      model.products.filter((p) => sel.products[p.id] && p.status !== "Cancelled").forEach((p) =>
        out.push({ id: `prod-${p.id}`, productId: p.id, kind: "product", label: p.label, productLabel: p.type, sell: p.sell, cost: p.cost, vat: p.vat, regime: p.regime }));
    } else {
      // partial: selected products (whole), entities, ssrs, travelers
      const wholeProductIds = new Set();
      model.products.filter((p) => sel.products[p.id] && p.status !== "Cancelled").forEach((p) => {
        wholeProductIds.add(p.id);
        out.push({ id: `prod-${p.id}`, productId: p.id, kind: "product", label: p.label, productLabel: p.type, sell: p.sell, cost: p.cost, vat: p.vat, regime: p.regime });
      });
      // Travelers ticked alongside a flight leg SCOPE that leg to them — they are not
      // a second, separate cancellation. Without this the leg is priced once in full
      // and then again in full per traveler, so cancelling one €460 leg for three
      // travelers offered a €1.840 refund.
      const scopingActive = travelerScopeActive(sel);
      Object.values(sel.entities).filter(Boolean).forEach((e) => {
        if (wholeProductIds.has(e.productId)) return;
        const p = model.products.find((x) => x.id === e.productId);
        const picked = (e.travelers || []).filter((t) => sel.travelers[t.id]);
        if (scopingActive && e.kind === "direction" && picked.length > 0) {
          // One line per (leg × traveler), priced at that traveler's share of the leg.
          picked.forEach((t) => out.push({
            id: `${e.id}-t${t.id}`, productId: e.productId, kind: "direction", direction: e.direction,
            label: `${e.label} — ${t.name}`, productLabel: p?.label || "",
            sell: t.sell, cost: t.cost, vat: t.vat, travelerId: t.id, scopedTraveler: true,
          }));
          return;
        }
        out.push({ id: e.id, productId: e.productId, kind: e.kind, label: e.label, productLabel: p?.label || "", sell: e.sell, cost: e.cost, vat: e.vat, travelerId: e.travelerId });
      });
      Object.values(sel.ssrs).filter(Boolean).forEach((s) => {
        if (wholeProductIds.has(s.productId)) return;
        const p = model.products.find((x) => x.id === s.productId);
        out.push({ id: s.id, productId: s.productId, kind: "ssr", label: s.label, productLabel: p?.label || "SSR", sell: s.sell, cost: s.cost, vat: s.vat });
      });
      // Whole-booking traveler cancellations — only when the ticked travelers aren't
      // already scoping a selected flight leg (above).
      if (!scopingActive) {
        Object.keys(sel.travelers).filter((k) => sel.travelers[k]).forEach((tid) => {
          const t = model.travelers.find((x) => String(x.id) === String(tid));
          if (t) out.push({ id: `trav-${tid}`, travelerId: tid, kind: "pax", label: t.name, productLabel: "Traveler", sell: num(t.sell), cost: num(t.cost), vat: num(t.vat), isTraveler: true });
        });
      }
    }
    return out;
  }, [model, scope, sel]);

  const selectedCount = useMemo(() => {
    if (scope === "full") return model ? model.products.length + model.ssrs.length : 0;
    return lines.length;
  }, [scope, lines, model]);

  // ── Totals from fees ──
  const lineCalcs = useMemo(() => lines.map((ln) => {
    const f = fees[ln.id] || { cost: "", fee: "" };
    const cc = clamp(num(f.cost), 0, Infinity);
    const cf = clamp(num(f.fee), 0, Infinity);
    // The Sunsky cancellation fee is VAT-inclusive: the VAT sits INSIDE the fee, so
    // the cancellation total = supplier cost + fee (VAT is NOT added on top). cv is the
    // VAT portion contained within the fee, surfaced for the credit note / VAT reporting.
    const cv = cf * VAT_INCLUSIVE_FRACTION;
    const cancTotal = cc + cf;
    const refund = ln.sell - cancTotal;
    return { ...ln, cancCost: cc, cancFee: cf, cancVat: cv, cancTotal, refund };
  }), [lines, fees]);

  const totals = useMemo(() => {
    const t = lineCalcs.reduce((a, l) => {
      a.cancelledSelling += l.sell; a.cancelledCost += l.cost;
      a.cancellationCost += l.cancCost; a.cancellationFee += l.cancFee;
      a.cancellationVat += l.cancVat; a.cancellationTotal += l.cancTotal;
      if (l.refund >= 0) a.refundDue += l.refund; else a.additionalDue += -l.refund;
      return a;
    }, { cancelledSelling: 0, cancelledCost: 0, cancellationCost: 0, cancellationFee: 0, cancellationVat: 0, cancellationTotal: 0, refundDue: 0, additionalDue: 0 });
    t.net = t.refundDue - t.additionalDue;
    return t;
  }, [lineCalcs]);

  const doc = totals.net > 0.005 ? "credit" : totals.net < -0.005 ? "debit" : "none";
  const docNum = model ? (doc === "credit" ? `CN-${model.invoiceNumber.replace(/^INV-/, "")}/1` : doc === "debit" ? `DN-${model.invoiceNumber.replace(/^INV-/, "")}/1` : "—") : "—";

  const reasonValid = !!reason && (reason !== "Other (see notes)" || !!notes.trim());

  const canProceed = useMemo(() => {
    if (step === 1) return !!model && model.bookingStatus !== "Cancelled" && !detailLoading;
    if (step === 2) return !!scope;
    if (step === 3) return scope === "full" ? true : lines.length > 0;
    if (step === 4) return true;
    if (step === 5) return true;
    if (step === 6) return reasonValid;
    return true;
  }, [step, model, detailLoading, scope, lines, reasonValid]);

  const cancelBookingApi = useCancelBooking();
  const cancelProductApi = useCancelBookingProduct();
  const cancelFlightLegsApi = useCancelFlightLegs();
  const travelerModApi = useCreateTravelerModification();
  const productModApi = useCreateProductModification();
  const createCancellationCaseApi = useCreateCancellationCase();

  const composedReason = () => {
    const parts = [reason];
    if (notes) parts.push(`Notes: ${notes}`);
    parts.push(`Cancellation ${fmt(totals.cancellationTotal)} | ${doc === "credit" ? `Credit ${docNum} refund ${fmt(totals.net)} (${refundMethod})` : doc === "debit" ? `Debit ${docNum} additional ${fmt(-totals.net)}` : "no document"}`);
    return parts.join(" | ");
  };

  // ── Confirm orchestration ──
  const handleConfirm = async () => {
    if (!model || submitting) return;
    setSubmitting(true);
    setResult(null);
    const reasonStr = composedReason();
    const steps = { case: "done" };
    const failures = [], warnings = [];
    let okCount = 0;
    let hardBlocked = false;
    try {
      if (scope === "full") {
        // Carry the per-product retained cancellation amounts so the backend stores
        // them on each product (fold into totals + Cancellation Effects), like the
        // product-scope path. SSR-line fees are aggregated onto their parent product.
        const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
        const pcMap = {};
        lineCalcs.forEach((l) => {
          if (l.productId == null) return;
          const k = String(l.productId);
          if (!pcMap[k]) pcMap[k] = { productId: l.productId, cost: 0, fee: 0, vat: 0 };
          pcMap[k].cost += l.cancCost || 0;
          pcMap[k].fee += l.cancFee || 0;
          pcMap[k].vat += l.cancVat || 0;
        });
        const productCancellations = Object.values(pcMap).map((p) => ({
          productId: p.productId, cost: round2(p.cost), fee: round2(p.fee), vat: round2(p.vat),
        }));
        const res = await cancelBookingApi.execute(model.id, { cancellationReason: reasonStr, productCancellations });
        if (res.success) {
          okCount += 1; steps.product = "done"; steps.traveler = "done"; steps.totals = "done"; steps.audit = "done";
          (res.warnings || []).forEach((w) => warnings.push(w.message || w));
          showToast(`Booking ${model.ref} cancelled`, "success");
        } else if (res.blockType === "HARD_BLOCK") {
          steps.product = "failed"; failures.push(`Governance block: ${res.message}`); showToast(res.message, "error");
        } else { steps.product = "failed"; failures.push(res.message || "Failed to cancel booking"); showToast(res.message || "Failed", "error"); }
      } else {
        // products — include products referenced by selected entities AND by selected SSRs
        const prodIds = scope === "product"
          ? model.products.filter((p) => sel.products[p.id]).map((p) => p.id)
          : [...new Set([
              ...model.products.filter((p) => sel.products[p.id]).map((p) => p.id),
              ...Object.values(sel.entities).filter(Boolean).map((e) => e.productId),
              ...Object.values(sel.ssrs).filter(Boolean).map((s) => s.productId),
            ])];
        // For partial: cancel the whole product only if ALL its entities are selected; else record a modification
        for (const pid of prodIds) {
          if (hardBlocked) break;
          const p = model.products.find((x) => x.id === pid);
          const allEntities = p ? p.entities.length : 0;
          const selEntities = Object.values(sel.entities).filter((e) => e && e.productId === pid).length;
          // Flight DIRECTION selections always route through the dedicated cancel-legs
          // path (which itself rolls up to a whole-flight cancel once the last direction
          // is gone), so never treat them as a whole-product cancel here.
          const hasDirectionSel = Object.values(sel.entities).some((e) => e && e.productId === pid && e.kind === "direction");
          const wholeProduct = scope === "product" || !!sel.products[pid] || (!hasDirectionSel && allEntities > 0 && selEntities >= allEntities);
          if (wholeProduct) {
            // Persist the per-product supplier cancellation cost / Sunsky fee so the
            // booking-detail Payments tab can show the supplier refund due.
            const lcP = lineCalcs.filter((l) => l.productId === pid && l.kind !== "ssr" && !l.isTraveler);
            const cancellationCost = lcP.reduce((s, l) => s + l.cancCost, 0);
            const cancellationFee = lcP.reduce((s, l) => s + l.cancFee, 0);
            const cancellationVat = lcP.reduce((s, l) => s + l.cancVat, 0);
            const res = await cancelProductApi.execute(model.id, pid, { cancellationReason: reasonStr, cancelAssignedTravelers: true, cancellationCost, cancellationFee, cancellationVat });
            if (res.success) { okCount += 1; steps.product = "done"; steps.totals = "done"; steps.audit = "done"; }
            else if (res.blockType === "HARD_BLOCK") { hardBlocked = true; steps.product = "failed"; failures.push(`Governance block — ${p?.label || pid}: ${res.message}`); showToast(res.message, "error"); }
            else failures.push(`${p?.label || pid}: ${res.message}`);
          } else if (p?.type === "Flight" && Object.values(sel.entities).some((e) => e && e.productId === pid && e.kind === "direction")) {
            // DIRECTION-LEVEL flight cancellation — cancel only the selected direction(s)
            // (e.g. outbound only) while the rest of the flight stays active. Works for every
            // flight type: SPLIT drops the direction's pricing exactly; FULL prorates it.
            const dirEnts = Object.values(sel.entities).filter((e) => e && e.productId === pid && e.kind === "direction");
            // Travelers ticked against these legs scope the cancel to them; none ticked
            // ⇒ omit travelerIds and the backend cancels the leg for the whole cabin.
            const scopedIds = travelerScopeActive(sel)
              ? [...new Set(dirEnts.flatMap((e) => (e.travelers || []).filter((t) => sel.travelers[t.id]).map((t) => String(t.id))))]
              : [];
            const lcDir = lineCalcs.filter((l) => l.productId === pid && l.kind === "direction");
            const cancellationCost = lcDir.reduce((s, l) => s + l.cancCost, 0);
            const cancellationFee = lcDir.reduce((s, l) => s + l.cancFee, 0);
            const cancellationVat = lcDir.reduce((s, l) => s + l.cancVat, 0);
            const refundAmount = lcDir.reduce((s, l) => s + l.refund, 0);
            const res = await cancelFlightLegsApi.execute(model.id, pid, {
              directions: dirEnts.map((e) => e.direction),
              ...(scopedIds.length ? { travelerIds: scopedIds } : {}),
              cancellationReason: reasonStr,
              cancellationCost, cancellationFee, cancellationVat, refundAmount,
              cancelAssignedTravelers: true,
            });
            if (res.success) {
              okCount += 1; steps.product = "done"; steps.totals = "done"; steps.audit = "done";
              const dirs = dirEnts.map((e) => e.direction).join(", ");
              if (res.data?.cancelledSsrs > 0) warnings.push(`${p?.label || pid}: ${res.data.cancelledSsrs} SSR(s) on the cancelled leg(s) were cancelled with it.`);
              showToast(
                res.data?.allCancelled ? `Flight fully cancelled`
                  : scopedIds.length ? `Flight ${dirs} cancelled for ${scopedIds.length} traveler(s)`
                  : `Flight ${dirs} cancelled`,
                "success"
              );
            } else if (res.blockType === "HARD_BLOCK") {
              hardBlocked = true; steps.product = "failed"; failures.push(`Governance block — ${p?.label || pid}: ${res.message}`); showToast(res.message, "error");
            } else { failures.push(`${p?.label || pid}: ${res.message}`); showToast(res.message || "Failed to cancel flight direction", "error"); }
          } else {
            // partial component — record a product modification (financial intent + audit).
            // refundAmount is signed: negative means an additional amount is due from the customer.
            const lc = lineCalcs.filter((l) => (l.productId === pid && l.kind !== "ssr" && !l.isTraveler) || (l.kind === "ssr" && l.productId === pid));
            const supplierCost = lc.reduce((s, l) => s + l.cancCost, 0);
            const agencyFee = lc.reduce((s, l) => s + l.cancFee, 0);
            const refundAmount = lc.reduce((s, l) => s + l.refund, 0);
            const vatAmount = lc.reduce((s, l) => s + l.cancVat, 0);
            const res = await productModApi.execute(model.id, pid, {
              modificationType: "CANCELLATION_FEE",
              modificationTitle: `Partial cancellation — ${reason}`,
              internalNotes: reasonStr, supplierCost, agencyFee, refundAmount, vatAmount,
              regenerateDocuments: true, notifyCustomer: true,
            });
            if (res.success) { okCount += 1; steps.product = "done"; steps.audit = "done"; warnings.push(`${p?.label || pid}: partial component cancellation recorded as a modification (per-component execution pending backend).`); }
            else if (res.blockType === "HARD_BLOCK") { hardBlocked = true; steps.product = "failed"; failures.push(`Governance block — ${p?.label || pid}: ${res.message}`); showToast(res.message, "error"); }
            else failures.push(`${p?.label || pid}: ${res.message}`);
          }
        }
        // travelers — carry the real supplier cost / Sunsky fee / customer refund so
        // the traveler cancellation folds its retained fee into the booking totals
        // (backend recalculateBookingTotals) and the credit note / refund records
        // reflect it. refundAmount is the customer refund (the traveler's share minus
        // the retained cancellation total); never negative here.
        const round2t = (n) => Math.round((Number(n) || 0) * 100) / 100;
        // When ticked travelers are scoping a flight leg they were already cancelled
        // off that leg above — cancelling them booking-wide here as well would remove
        // them from every other product too.
        const travIds = travelerScopeActive(sel) ? [] : Object.keys(sel.travelers).filter((k) => sel.travelers[k]);
        for (const tid of travIds) {
          if (hardBlocked) break;
          const t = model.travelers.find((x) => String(x.id) === String(tid));
          const tl = lineCalcs.find((l) => l.isTraveler && String(l.travelerId) === String(tid));
          const res = await travelerModApi.execute(model.id, tid, {
            actionType: "CANCEL_TRAVELER", modificationTitle: `Cancellation — ${reason}`, internalNotes: reasonStr,
            supplierCost: round2t(tl?.cancCost || 0),
            agencyFee: round2t(tl?.cancFee || 0),
            refundAmount: round2t(Math.max(0, tl?.refund || 0)),
            regenerateDocuments: true, notifyCustomer: true,
          });
          if (res.success) { okCount += 1; steps.traveler = "done"; steps.audit = "done"; }
          else if (res.blockType === "HARD_BLOCK") { hardBlocked = true; failures.push(`Governance block — ${t?.name || tid}: ${res.message}`); showToast(res.message, "error"); }
          else failures.push(`${t?.name || tid}: ${res.message}`);
        }
      }

      const ok = failures.length === 0;

      // Persist the cancellation CASE (scope, financial summary, refund method,
      // per-item lines) and issue the credit/debit note document. This runs only
      // once the actual cancellations succeeded; a failure here is surfaced as a
      // warning (the cancellation itself already went through and was re-costed).
      if (ok) {
        try {
          // Round every amount to 2 decimals so floating-point noise (e.g.
          // 86.58000000000001) never reaches the payload — the backend rounds too.
          const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
          const caseLines = lineCalcs.map((l) => ({
            productId: l.productId ?? null,
            label: l.label ?? null,
            itemType: l.type || l.kind || null,
            originalSell: r2(l.sell),
            cancCost: r2(l.cancCost),
            cancFee: r2(l.cancFee),
            cancVat: r2(l.cancVat),
            customerRefund: r2(Math.max(0, l.refund || 0)),
            supplierRefundDue: r2(Math.max(0, (l.cost || 0) - (l.cancCost || 0))),
          }));
          const supplierRefundDueTotal = r2(caseLines.reduce((s, l) => s + l.supplierRefundDue, 0));
          const caseRes = await createCancellationCaseApi.execute(model.id, {
            scope,
            caseType: scope === "full" ? "Booking" : scope === "product" ? "Product" : "Partial",
            reason,
            notes,
            refundMethod,
            documentDecision: doc,
            customerName: model.customer?.name,
            cancellationCost: r2(totals.cancellationCost),
            cancellationFee: r2(totals.cancellationFee),
            cancellationVat: r2(totals.cancellationVat),
            cancellationTotal: r2(totals.cancellationTotal),
            refundDue: r2(totals.refundDue),
            additionalDue: r2(totals.additionalDue),
            supplierRefundDue: supplierRefundDueTotal,
            lines: caseLines,
          });
          if (caseRes.success) {
            steps.case = "done";
            (caseRes.warnings || []).forEach((w) => warnings.push(w));
            if (caseRes.documentNumber) {
              steps.cn = "done";
              steps.docs = "done";
              showToast(`Document ${caseRes.documentNumber} issued`, "success");
            }
            // Pending refund records auto-created by the backend with the case
            if (caseRes.clientRefund?.created) {
              steps.client = "done";
              showToast(`Client refund ${fmt(caseRes.clientRefund.amount)} recorded (pending in Finance)`, "success");
            }
            if (caseRes.supplierRefund?.created) {
              steps.supplier = "done";
              showToast(`Supplier refund ${fmt(caseRes.supplierRefund.amount)} recorded — booking shows "Waiting Supplier Refund" until received`, "success");
            }
          } else {
            steps.case = "failed";
            warnings.push(
              `Cancellation done, but the credit note and refund records were NOT created: ${caseRes.message}. ` +
              `The booking is already cancelled — go to Finance → Payments and manually register the client/supplier refund for ${model.ref} (Cancellation ${fmt(totals.cancellationTotal)}, refund due ${fmt(totals.refundDue)}), or retry recording the case from this booking's Documents tab.`
            );
            showToast(`Refund records were not created for ${model.ref} — see the warning below and register them manually in Finance.`, "error");
          }
        } catch (caseErr) {
          steps.case = "failed";
          warnings.push(
            `Cancellation done, but the credit note and refund records were NOT created: ${caseErr.message}. ` +
            `The booking is already cancelled — go to Finance → Payments and manually register the client/supplier refund for ${model.ref} (Cancellation ${fmt(totals.cancellationTotal)}, refund due ${fmt(totals.refundDue)}), or retry recording the case from this booking's Documents tab.`
          );
          showToast(`Refund records were not created for ${model.ref} — see the warning below and register them manually in Finance.`, "error");
        }
      }

      if (ok) showToast(`Cancellation case ${`CAN-${(model.ref || "").replace(/^SNK-?/, "")}/1`} completed`, "success");
      else showToast(`Completed with ${failures.length} issue(s)`, "error");
      setResult({ ok, okCount, failCount: failures.length, failures, warnings, steps });
    } catch (err) {
      showToast(err.message || "Cancellation failed", "error");
      setResult({ ok: false, okCount, failCount: 1, failures: [err.message || "Cancellation failed"], warnings, steps });
    } finally {
      setSubmitting(false); window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNext = () => goStep(Math.min(7, step + 1));
  // From the first actionable step of a deep-linked cancellation, Back leaves the
  // flow (returns to the originating List/Detail) rather than the locked booking card.
  const handleBack = () => { if (deepLinked && step === 2) return navigate(-1); goStep(Math.max(1, step - 1)); };
  const handleSelect = (id) => setSelectedId(id);

  const confirmedDone = !!result;

  return (
    <div className={styles.container}>
      <button className={styles.backLink} onClick={() => navigate(-1)}><ArrowLeft size={16} /> Back</button>

      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}><Ban size={24} /></div>
          <div><h1 className={styles.headerTitle}>Cancellation Engine</h1><p className={styles.headerDesc}>Full, product and partial cancellations with financial, document and status management</p></div>
        </div>
        <div className={styles.headerRight}>{model && <span className={styles.refPill}>{model.ref}</span>}<button className={styles.btnSecondary} onClick={() => navigate(-1)}><X size={14} /> Discard</button></div>
      </div>

      <StepIndicator current={step} maxReached={maxReached} onJump={(n) => !result && goStep(n)} />

      <div className={styles.body}>
        <div className={styles.mainContent}>
          <div className={styles.stepCard}>
            {step === 1 && <StepBooking query={query} setQuery={setQuery} results={results} loading={listLoading} error={listError} selectedId={selectedId} onSelect={handleSelect} model={model} detailLoading={detailLoading} detailError={detailError} locked={deepLinked} />}
            {step === 2 && model && <StepScope model={model} scope={scope} onScope={onScope} />}
            {step === 3 && model && <StepItems model={model} scope={scope} sel={sel} onToggleProduct={onToggleProduct} onToggleEntity={onToggleEntity} onToggleTraveler={onToggleTraveler} onToggleSsr={onToggleSsr} selectedCount={selectedCount} />}
            {step === 4 && model && <StepPricing lines={lines} fees={fees} onFee={(id, v) => setFees((f) => ({ ...f, [id]: v }))} />}
            {step === 5 && model && <StepFinancial model={model} totals={totals} />}
            {step === 6 && model && <StepDocuments model={model} totals={totals} refundMethod={refundMethod} onRefundMethod={setRefundMethod} reason={reason} onReason={setReason} notes={notes} onNotes={setNotes} />}
            {step === 7 && model && <StepConfirm model={model} totals={totals} lines={lineCalcs} scope={scope} reason={reason} doc={doc} docNum={docNum} onConfirm={handleConfirm} submitting={submitting} result={result} />}
          </div>
        </div>
        <SummaryPanel model={model} scope={scope} step={step} selectedCount={selectedCount} totals={step >= 4 ? totals : null} />
      </div>

      {!confirmedDone && (
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <span className={styles.footerStepText}>Step {step} of {STEPS.length}</span>
            <div className={styles.footerTrack}><div className={styles.footerFill} style={{ width: `${((step - 1) / (STEPS.length - 1)) * 100}%` }} /></div>
          </div>
          <div className={styles.footerActions}>
            {step > 1 && <button className={styles.btnSecondary} onClick={handleBack} disabled={submitting}><ChevronLeft size={15} /> Back</button>}
            {step < 7 && <button className={`${styles.btnPrimary} ${!canProceed ? styles.btnDisabled : ""}`} onClick={handleNext} disabled={!canProceed}>{step === 6 ? "Proceed to Confirm" : "Next"} <ChevronRight size={15} /></button>}
          </div>
        </div>
      )}
      {confirmedDone && (
        <div className={styles.footer}>
          <div className={styles.footerActions} style={{ marginLeft: "auto" }}>
            <button className={styles.btnSecondary} onClick={() => navigate("/bookings")}>View All Bookings</button>
            {model && <button className={styles.btnPrimary} onClick={() => navigate(`/bookings/${model.id}`)}>Open Booking <ChevronRight size={15} /></button>}
          </div>
        </div>
      )}
    </div>
  );
};

export default CancellationFlow;
